/**
 * Traffic › Live (`/traffic/live`) — who's on the site right now.
 *
 * One `/v1/sites/{uuid}/pixel/live` read per poll now carries the whole
 * page: headline counts, a per-minute pageview pulse (last 30 minutes),
 * top pages / sources / countries, and CWV p75s for the visitors
 * currently on the site. Still deliberately a glance-at-it surface: no
 * tables, no filters, no range picker — the window is always "the last
 * five minutes" (the pulse alone looks back 30).
 *
 * COST NOTE — read before widening this. Every poll is a Tinybird read.
 * The workspace is on a paid plan now, so this is about not fetching
 * data nobody asked for rather than about a hard ceiling.
 *
 * The distinction that matters is *who is watching*. The shell's live
 * pill polls on every page for every open tab whether or not anyone
 * cares, which is why it's still switched off (`LIVE_POLLING_ENABLED`
 * in `lib/queries/pixel.ts`) — at ~2,880 reads/day/tab it was paying
 * for decoration. This page is the opposite: someone navigated here on
 * purpose to watch. So it polls, but only while the tab is visible and
 * only for as long as a person plausibly is watching (see the pause
 * below). Keep that shape if you change the numbers — the richer
 * payload rides the SAME single read per poll (`pixel_live_detail`
 * returns every section in one result set for exactly this reason).
 */
import { SiteIsland } from '@/components/shell/SiteIsland';
import { Button } from '@/components/ui/button';
import { useActiveSiteUuid } from '@/lib/active-site';
import { countryName } from '@/lib/country-name';
import {
  type MetricBand,
  bandText,
  clsBand,
  formatCls,
  formatMs,
  inpBand,
  lcpBand,
} from '@/lib/format';
import {
  type PixelLiveListRow,
  type PixelLiveMinute,
  type PixelLiveVital,
  useSitePixelLive,
} from '@/lib/queries/pixel';
import { cn } from '@/lib/utils';
import { Link, createFileRoute } from '@tanstack/react-router';
import { type ReactElement, useEffect, useRef, useState } from 'react';

export const Route = createFileRoute('/_app/traffic/live/')({
  component: LivePage,
});

/** Fast enough to read as live, slow enough to stay cheap. */
const POLL_MS = 15_000;

/**
 * How long a visit keeps polling before asking whether anyone's still
 * there. Long enough to sit through the thing you opened this page to
 * watch — a deploy, a campaign send — without being interrupted;
 * short enough that a tab left open all weekend stops on its own.
 */
const SESSION_MS = 15 * 60_000;

/** Minutes shown in the pulse chart — matches the pipe's window. */
const PULSE_MINUTES = 30;

const fmt = new Intl.NumberFormat('en');

function LivePage(): ReactElement {
  const siteUuid = useActiveSiteUuid();
  const [running, setRunning] = useState(true);

  const live = useSitePixelLive(siteUuid, { enabled: running, intervalMs: POLL_MS });

  // Stop polling once the visit's budget is spent. Restarting via
  // "Resume" re-arms a fresh window, so someone actively watching can
  // keep going indefinitely — they just have to still be there.
  useEffect(() => {
    if (!running) return;
    const timer = setTimeout(() => setRunning(false), SESSION_MS);
    return () => clearTimeout(timer);
  }, [running]);

  // `dataUpdatedAt` only moves on a successful fetch, so this ticks from
  // the last good response rather than the last attempt.
  const updatedAt = live.dataUpdatedAt;
  const since = useSecondsSince(updatedAt, running);

  const visitors = live.data?.active_visitors ?? 0;
  const sessions = live.data?.active_sessions ?? 0;
  const events = live.data?.beacons_5m ?? 0;
  // Distinguish "nothing yet" from a real zero: dashes until the first
  // response lands, so an empty site and a loading one don't look alike.
  const settled = live.isSuccess;

  const vitals = live.data?.vitals;

  return (
    <SiteIsland section="Live" parent={{ label: 'Traffic', href: '/traffic' }}>
      {siteUuid && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <StatusLine running={running} settled={settled} secondsSince={since} />
            <Button variant="outline" size="sm" onClick={() => setRunning((r) => !r)}>
              {running ? 'Pause' : 'Resume'}
            </Button>
          </div>

          <div
            className={cn(
              'grid gap-px overflow-hidden rounded-xl border border-[var(--color-line)]',
              'grid-cols-1 bg-[var(--color-line)] sm:grid-cols-3',
            )}
          >
            <LiveTile label="Active visitors" value={settled ? fmt.format(visitors) : '—'} />
            <LiveTile label="Active sessions" value={settled ? fmt.format(sessions) : '—'} />
            <LiveTile label="Events" value={settled ? fmt.format(events) : '—'} />
          </div>

          <PulseChart minutes={live.data?.minutes} settled={settled} />

          <div
            className={cn(
              'grid gap-px overflow-hidden rounded-xl border border-[var(--color-line)]',
              'grid-cols-1 bg-[var(--color-line)] sm:grid-cols-3',
            )}
          >
            <VitalTile
              label="LCP"
              vital={vitals?.lcp}
              settled={settled}
              band={lcpBand}
              format={formatMs}
            />
            <VitalTile
              label="INP"
              vital={vitals?.inp}
              settled={settled}
              band={inpBand}
              format={formatMs}
            />
            <VitalTile
              label="CLS"
              vital={vitals?.cls}
              settled={settled}
              band={clsBand}
              format={formatCls}
            />
          </div>

          <div className="grid gap-3 lg:grid-cols-3">
            <LiveList
              title="Pages"
              rows={live.data?.top_pages}
              settled={settled}
              renderLabel={(r) => r.label}
              linkPage
            />
            <LiveList
              title="Sources"
              rows={live.data?.sources}
              settled={settled}
              renderLabel={(r) => (r.label === '' ? 'Direct' : r.label)}
            />
            <LiveList
              title="Countries"
              rows={live.data?.countries}
              settled={settled}
              renderLabel={(r) => {
                const flag = flagEmoji(r.label);
                return flag ? `${flag}  ${countryName(r.label)}` : countryName(r.label);
              }}
            />
          </div>

          <p className="text-xs text-[var(--color-mute)]">
            {settled && visitors === 0
              ? 'Nobody on the site in the last five minutes.'
              : 'Counts, vitals and lists cover the last five minutes; the pulse looks back thirty.'}
          </p>
        </div>
      )}
    </SiteIsland>
  );
}

/** Seconds elapsed since `timestamp`, re-rendering every second while
 *  `active`. Returns null before the first successful response. */
function useSecondsSince(timestamp: number, active: boolean): number | null {
  const [, force] = useState(0);
  const frame = useRef(0);
  useEffect(() => {
    if (!active || !timestamp) return;
    const id = setInterval(() => {
      frame.current += 1;
      force(frame.current);
    }, 1000);
    return () => clearInterval(id);
  }, [active, timestamp]);
  if (!timestamp) return null;
  return Math.max(0, Math.round((Date.now() - timestamp) / 1000));
}

function StatusLine({
  running,
  settled,
  secondsSince,
}: {
  running: boolean;
  settled: boolean;
  secondsSince: number | null;
}): ReactElement {
  if (!running) {
    return (
      <p className="text-sm text-[var(--color-mute)]">Updates paused. Resume to keep watching.</p>
    );
  }
  return (
    <p className="flex items-center gap-2 text-sm text-[var(--color-mute)]">
      <span
        aria-hidden="true"
        className="inline-block size-1.5 shrink-0 animate-pulse rounded-full bg-emerald-500"
      />
      <span className="font-medium text-[var(--color-ink)]">Live</span>
      {settled && secondsSince !== null && (
        <>
          <span className="text-[var(--color-line)]">·</span>
          <span>{secondsSince < 5 ? 'just now' : `updated ${secondsSince}s ago`}</span>
        </>
      )}
    </p>
  );
}

function LiveTile({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div className="flex min-w-0 flex-col gap-2 bg-[var(--color-paper)] p-4">
      <span className="truncate text-xs font-medium text-[var(--color-mute)]">{label}</span>
      <span className="text-3xl font-bold leading-[1.1] tabular-nums text-[var(--color-ink)]">
        {value}
      </span>
    </div>
  );
}

// ─── Pulse chart ─────────────────────────────────────────────────────

/**
 * Per-minute pageview bars for the trailing 30 minutes. Plain flex
 * divs rather than SVG — 30 equal columns scaling to any width is
 * exactly what flexbox does, and it keeps the rounded bar tops from
 * distorting the way `preserveAspectRatio="none"` would.
 *
 * The newest bar is the in-progress minute, so it grows across polls;
 * that's deliberate — a filling bar is the "live" tell.
 */
function PulseChart({
  minutes,
  settled,
}: {
  minutes: PixelLiveMinute[] | undefined;
  settled: boolean;
}): ReactElement {
  // Fill gaps: the pipe only returns minutes that saw traffic. Build
  // the full trailing window ending at the current minute so quiet
  // minutes render as gaps rather than compressing time.
  const byMinute = new Map((minutes ?? []).map((m) => [m.minute, m]));
  const endMs = Math.max(Date.now(), ...(minutes ?? []).map((m) => new Date(m.minute).getTime()));
  const end = new Date(endMs);
  end.setUTCSeconds(0, 0);
  const slots: { minute: string; pageviews: number; visitors: number }[] = [];
  for (let i = PULSE_MINUTES - 1; i >= 0; i--) {
    const d = new Date(end.getTime() - i * 60_000);
    const iso = `${d.toISOString().slice(0, 19)}Z`;
    const hit = byMinute.get(iso);
    slots.push({ minute: iso, pageviews: hit?.pageviews ?? 0, visitors: hit?.visitors ?? 0 });
  }
  const max = Math.max(1, ...slots.map((s) => s.pageviews));
  const peak = Math.max(...slots.map((s) => s.pageviews));
  const timeFmt = new Intl.DateTimeFormat('en', { hour: 'numeric', minute: '2-digit' });

  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-xs font-medium text-[var(--color-mute)]">Pageviews per minute</h3>
        <span className="text-[10px] tabular-nums text-[var(--color-mute)]">
          {settled && peak > 0 ? `peak ${fmt.format(peak)}/min` : ' '}
        </span>
      </div>
      <div className="mt-3 flex h-40 items-end gap-[3px]" aria-hidden={!settled}>
        {slots.map((s) => {
          const pct = (s.pageviews / max) * 100;
          return (
            <div
              key={s.minute}
              className="group relative flex h-full flex-1 items-end"
              title={
                settled
                  ? `${timeFmt.format(new Date(s.minute))} — ${fmt.format(s.pageviews)} pageview${s.pageviews === 1 ? '' : 's'}, ${fmt.format(s.visitors)} visitor${s.visitors === 1 ? '' : 's'}`
                  : undefined
              }
            >
              <div
                className={cn(
                  'w-full rounded-t-[2px] transition-[height] duration-500',
                  s.pageviews > 0 && settled
                    ? 'bg-[var(--color-accent-brand)]'
                    : 'bg-[var(--color-line)]',
                )}
                style={{ height: s.pageviews > 0 && settled ? `${Math.max(3, pct)}%` : '2px' }}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-1.5 flex justify-between text-[10px] text-[var(--color-mute)]">
        <span>30 min ago</span>
        <span>15 min ago</span>
        <span>now</span>
      </div>
    </section>
  );
}

// ─── Vitals ──────────────────────────────────────────────────────────

/**
 * CWV p75 for the visitors currently on the site — the reason to watch
 * this page during a deploy. Colour carries the verdict; the sample
 * count lives in the tooltip so the tile stays quiet.
 */
function VitalTile({
  label,
  vital,
  settled,
  band,
  format,
}: {
  label: string;
  vital: PixelLiveVital | undefined;
  settled: boolean;
  band: (v: number | null | undefined) => MetricBand;
  format: (v: number | null | undefined) => string;
}): ReactElement {
  const hasSample = settled && vital != null && vital.samples > 0 && vital.p75 != null;
  const title = hasSample
    ? `p75 across ${fmt.format(vital.samples)} measurement${vital.samples === 1 ? '' : 's'} in the last 5 minutes`
    : 'No measurements in the last 5 minutes';
  return (
    <div className="flex min-w-0 flex-col gap-2 bg-[var(--color-paper)] p-4" title={title}>
      <span className="truncate text-xs font-medium text-[var(--color-mute)]">{label} p75</span>
      <span
        className={cn(
          'text-2xl font-bold leading-[1.1] tabular-nums',
          hasSample ? bandText(band(vital.p75)) : 'text-[var(--color-mute)]',
        )}
      >
        {hasSample ? format(vital.p75) : '—'}
      </span>
    </div>
  );
}

// ─── Lists ───────────────────────────────────────────────────────────

/**
 * Top-5 list with the breakdown-card row language (proportional bar,
 * truncated label, tabular count) minus the chrome — no tabs, no
 * search, no footer. Five rows is the whole story for a 5-minute
 * window.
 */
function LiveList({
  title,
  rows,
  settled,
  renderLabel,
  linkPage = false,
}: {
  title: string;
  rows: PixelLiveListRow[] | undefined;
  settled: boolean;
  renderLabel: (row: PixelLiveListRow) => string;
  linkPage?: boolean;
}): ReactElement {
  const list = rows ?? [];
  const max = Math.max(1, ...list.map((r) => r.visitors));
  const rowGrid = 'relative grid h-full items-center gap-2 px-3 [grid-template-columns:1fr_auto]';
  return (
    <section className="flex flex-col rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]">
      <header className="flex items-baseline justify-between border-b border-[var(--color-line-soft)] px-3 py-2.5">
        <h3 className="text-sm font-semibold text-[var(--color-ink)]">{title}</h3>
        <span className="text-[10px] text-[var(--color-mute)]">visitors</span>
      </header>
      <ul className="flex-1">
        {settled && list.length > 0 ? (
          list.map((r) => {
            const label = renderLabel(r);
            const content = (
              <>
                <span
                  className="truncate text-xs text-[var(--color-ink-soft)]"
                  title={r.label || label}
                >
                  {label}
                </span>
                <span className="text-right text-xs tabular-nums text-[var(--color-ink-soft)]">
                  {fmt.format(r.visitors)}
                </span>
              </>
            );
            return (
              <li
                key={r.label}
                className="relative h-8 border-b border-[var(--color-line-soft)]/60 last:border-0"
              >
                <div
                  aria-hidden="true"
                  className="absolute inset-y-0 left-0 bg-[var(--color-bar)]/10"
                  style={{ width: `${Math.max(8, Math.round((r.visitors / max) * 100))}%` }}
                />
                {linkPage ? (
                  <Link
                    to="/traffic/page"
                    search={{ path: r.label }}
                    className={cn(rowGrid, 'hover:bg-[var(--color-paper-alt)]/60')}
                  >
                    {content}
                  </Link>
                ) : (
                  <div className={rowGrid}>{content}</div>
                )}
              </li>
            );
          })
        ) : settled ? (
          <li className="flex min-h-[10rem] items-center justify-center px-6 py-8 text-center text-xs text-[var(--color-mute)]">
            Quiet right now.
          </li>
        ) : (
          Array.from({ length: 5 }).map((_, i) => (
            <li
              // biome-ignore lint/suspicious/noArrayIndexKey: positional skeleton rows
              key={i}
              className="relative h-8 border-b border-[var(--color-line-soft)]/60 last:border-0"
            >
              <div
                aria-hidden="true"
                className="absolute inset-y-0 left-0 bg-[var(--color-bar)]/10 opacity-60"
                style={{ width: `${100 - i * 18}%` }}
              />
            </li>
          ))
        )}
      </ul>
    </section>
  );
}

/** ISO 3166-1 alpha-2 → flag emoji via regional-indicator pairs; ''
 *  when the code isn't two ASCII letters. */
function flagEmoji(code: string): string {
  const cc = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(...[...cc].map((c) => 127397 + c.charCodeAt(0)));
}
